import { ElementRef } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Utility functions for DOM event handling and related browser APIs.
 */

/**
 * Completely blocks an event from propagating or executing default behavior.
 * Useful for preventing click events on disabled or restricted elements.
 *
 * @param event - The DOM event to block
 *
 * @example
 * element.addEventListener('click', blockEvent, true);
 */
export function blockEvent(event: Event): void {
  event.stopPropagation();
  event.stopImmediatePropagation();
  event.preventDefault();
}

function resolveObservedElement(target: Element | ElementRef<Element>): Element {
  return 'nativeElement' in target ? target.nativeElement : target;
}

/**
 * Emits whenever `element` transitions from intersecting to not intersecting the observer root
 * (the viewport when `root` is omitted).
 *
 * Observer callbacks usually run outside Angular’s zone; use `enterZone` from
 * `src/app/utilities/ZoneUtils` when emissions must trigger change detection.
 *
 * @param element Host element or `ElementRef` to observe.
 * @param options Passed to `IntersectionObserver` (e.g. `root` for a scroll container).
 */
export function elementLeavesViewport$(
  element: Element | ElementRef<Element>,
  options?: IntersectionObserverInit,
): Observable<void> {
  const el = resolveObservedElement(element);

  return new Observable<void>((subscriber) => {
    let previousIntersecting: boolean | undefined;

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry) {
        return;
      }

      const { isIntersecting } = entry;
      if (previousIntersecting === true && !isIntersecting) {
        subscriber.next();
      }
      previousIntersecting = isIntersecting;
    }, options);

    observer.observe(el);

    return () => observer.disconnect();
  });
}
